import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { TRANSACTION_TYPES } from '../utils/formatters';

// RP code ascending (blank codes last), so the picker matches how the rest
// of the admin app orders investors. `numeric: true` keeps "2-00" before
// "10-00" instead of a plain string sort putting "10-00" first.
const sortInvestorsByRpCode = (investors) => [...investors].sort((a, b) => {
  const ca = a.risk_participant_code || '';
  const cb = b.risk_participant_code || '';
  if (!ca && !cb) return 0;
  if (!ca) return 1;
  if (!cb) return -1;
  return ca.localeCompare(cb, undefined, { numeric: true });
});

const investorDisplayName = (inv) =>
  inv.full_name || inv.entity_name || `${inv.first_name || ''} ${inv.last_name || ''}`.trim() || inv.email;

export const TransactionFormDialog = ({
  open,
  onOpenChange,
  editingTransaction,
  formData,
  setFormData,
  investors,
  submitting,
  handleSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{editingTransaction ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
        <DialogDescription>Record an investor capital movement</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Investor *</Label>
          <Select value={formData.investor_id} onValueChange={v => setFormData(p => ({...p, investor_id: v}))}>
            <SelectTrigger data-testid="form-investor" className="w-full"><SelectValue placeholder="Select investor" /></SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              {sortInvestorsByRpCode(investors).map(inv => (
                <SelectItem key={inv.id} value={inv.id} className="pr-8">
                  <span className="block truncate">
                    {inv.risk_participant_code ? `${inv.risk_participant_code} — ` : ''}
                    {investorDisplayName(inv)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Transaction Type *</Label>
          <Select value={formData.transaction_type} onValueChange={v => setFormData(p => ({...p, transaction_type: v}))}>
            <SelectTrigger data-testid="form-type" className="w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              {TRANSACTION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value} className="pr-8">
                  <span className="block truncate">{t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" min="0" placeholder="Enter amount" value={formData.amount} onChange={e => setFormData(p => ({...p, amount: e.target.value}))} data-testid="form-amount" /></div>
        <div className="space-y-2"><Label>Transaction Date *</Label><Input type="date" value={formData.transaction_date} onChange={e => setFormData(p => ({...p, transaction_date: e.target.value}))} data-testid="form-date" /></div>
        <div className="space-y-2"><Label>Reference</Label><Input placeholder="Reference number" value={formData.reference} onChange={e => setFormData(p => ({...p, reference: e.target.value}))} data-testid="form-reference" /></div>
        <div className="space-y-2"><Label>Notes</Label><Textarea placeholder="Additional notes" value={formData.notes} onChange={e => setFormData(p => ({...p, notes: e.target.value}))} data-testid="form-notes" /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-slate-900">{submitting ? 'Saving...' : editingTransaction ? 'Update' : 'Create'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
